import { useEffect, useState, useCallback } from 'react';
import { api, type AuditEntry, type PaginatedResponse } from '../lib/api';
import s from './AuditPage.module.css';

/* -------------------------------------------------------------------------- */
/*  AuditPage                                                                  */
/* -------------------------------------------------------------------------- */

export default function AuditPage() {
  const [data, setData] = useState<PaginatedResponse<AuditEntry> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const limit = 25;

  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');

  // Expanded detail rows
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchAudit = useCallback(() => {
    setLoading(true);
    const params: { page: number; limit: number; action?: string; userId?: string } = {
      page,
      limit,
    };
    if (actionFilter) params.action = actionFilter;
    if (userFilter) params.userId = userFilter;

    api.audit
      .list(params)
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load audit log');
        setLoading(false);
      });
  }, [page, actionFilter, userFilter]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [actionFilter, userFilter]);

  if (loading && !data) {
    return <div className={s.loading}>Loading audit log&hellip;</div>;
  }

  if (error) {
    return <div className={s.error}>{error}</div>;
  }

  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  return (
    <>
      {/* Header */}
      <div className={s.header}>
        <h1 className={s.title}>Audit Log</h1>
      </div>

      {/* Filters */}
      <div className={s.filters}>
        <select
          className={s.filterSelect}
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
        >
          <option value="">All actions</option>
          <option value="domain.create">domain.create</option>
          <option value="domain.delete">domain.delete</option>
          <option value="user.login">user.login</option>
          <option value="user.logout">user.logout</option>
          <option value="user.signup">user.signup</option>
          <option value="settings.update">settings.update</option>
          <option value="source.classify">source.classify</option>
        </select>

        <input
          type="text"
          className={s.filterInput}
          placeholder="Filter by user email..."
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
        />
      </div>

      {/* Table */}
      {!data || data.data.length === 0 ? (
        <div className={s.emptyState}>No audit entries found</div>
      ) : (
        <>
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((entry) => (
                  <>
                    <tr
                      key={entry.id}
                      className={entry.details ? s.clickableRow : undefined}
                      onClick={() => {
                        if (!entry.details) return;
                        setExpandedId(expandedId === entry.id ? null : entry.id);
                      }}
                    >
                      <td className={s.timestamp}>
                        {new Date(entry.createdAt).toLocaleString()}
                      </td>
                      <td className={s.email}>{entry.userEmail}</td>
                      <td className={s.action}>{entry.action}</td>
                      <td>
                        {entry.resource}
                        {entry.resourceId && (
                          <span style={{ color: 'var(--text-disabled)', marginLeft: 'var(--space-1)' }}>
                            ({entry.resourceId})
                          </span>
                        )}
                      </td>
                      <td style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
                        {entry.details ? 'Click to expand' : '—'}
                      </td>
                    </tr>
                    {expandedId === entry.id && entry.details && (
                      <tr key={`${entry.id}-details`} className={s.detailsRow}>
                        <td colSpan={5}>
                          <div className={s.detailsContent}>
                            {JSON.stringify(entry.details, null, 2)}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={s.pagination}>
              <button
                type="button"
                className={s.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </button>
              <span className={s.pageInfo}>
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className={s.pageBtn}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
